import { useState } from 'react';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';

import { S3_FOLDER } from '@/config/constants';
import { useGetSignedUrlMutation } from '@/apis/extended/globalApi';
import { useCreateProductMutation } from '@/apis/extended/productApi';
import { uploadStorageFile } from '@/utils/storage';
import { CommonDialog } from '../Common/CommonDialog';
import { CommonFormControl } from '../Common/CommonFormControl';
import { CommonNumberInput } from '../Common/CommonNumberInput';
import { ProductPhotoField } from '../Common/ProductPhotoField';

const initialForm = {
  name: '',
  price: '',
  offerPrice: '',
  description: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
  },
};

type CreateProductModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateProductModal = ({
  isOpen,
  onClose,
}: CreateProductModalProps) => {
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const [getSignedUrl] = useGetSignedUrlMutation();
  const [form, setForm] = useState({ ...initialForm });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const isValid = Boolean(
    form.price &&
      form.address.street &&
      form.address.city &&
      form.address.state &&
      form.address.zip
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setForm({
        ...form,
        address: {
          ...form.address,
          [addressField]: value,
        },
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setForm({
        ...form,
        address: {
          ...form.address,
          [addressField]: value,
        },
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleImageChange = (file: File | null) => {
    setSelectedImage(file);
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImageUrl(previewUrl);
    } else {
      setImageUrl('');
    }
  };

  const handleCreateProduct = async () => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    let uploadedImageUrl = '';

    // Upload image if selected
    if (selectedImage) {
      setIsUploadingImage(true);
      try {
        uploadedImageUrl = await uploadStorageFile(
          selectedImage,
          S3_FOLDER.PRODUCT_IMAGE,
          () =>
            getSignedUrl({
              folderName: S3_FOLDER.PRODUCT_IMAGE,
              fileName: selectedImage.name,
            }).unwrap()
        );
      } catch (error) {
        console.error('Error uploading image:', error);
        setIsUploadingImage(false);
        return;
      }
      setIsUploadingImage(false);
    }

    // Generate fullAddress from components
    const fullAddress = `${form.address.street}, ${form.address.city}, ${form.address.state} ${form.address.zip}`;

    // Generate name from address if not provided
    const productName = form.name || fullAddress;

    const offerPrice = form.offerPrice
      ? parseFloat(form.offerPrice)
      : undefined;

    createProduct({
      name: productName,
      price: parseFloat(form.price),
      offerPrice,
      description: form.description || undefined,
      photo: uploadedImageUrl || undefined,
      address: {
        ...form.address,
        fullAddress,
      },
    })
      .unwrap()
      .then(() => {
        setForm({ ...initialForm });
        setSelectedImage(null);
        setImageUrl('');
        setShowErrors(false);
        onClose();
      })
      .catch((error) => {
        console.error('Error creating product:', error);
      });
  };

  const handleClose = () => {
    setForm({ ...initialForm });
    setSelectedImage(null);
    setImageUrl('');
    setShowErrors(false);
    onClose();
  };

  return (
    <CommonDialog
      isOpen={isOpen}
      title="Add Property"
      icon="fa-light fa-plus"
      confirmButtonText="Create Property"
      closeButtonText="Cancel"
      isLoading={isLoading || isUploadingImage}
      isConfirmDisabled={isLoading || isUploadingImage}
      onConfirm={handleCreateProduct}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '600px',
          maxWidth: '90vw',
        },
      }}
    >
      <Box>
        <ProductPhotoField
          file={selectedImage}
          imageUrl={imageUrl}
          onFileChange={handleImageChange}
        />

        <CommonFormControl
          label="Price:"
          sx={{ mt: 2 }}
          error={showErrors && !form.price ? 'This field is required!' : ''}
        >
          <CommonNumberInput
            placeholder="Price"
            name="price"
            fullWidth
            value={form.price}
            onChange={handleInputChange}
          />
        </CommonFormControl>

        <CommonFormControl label="Offer Price:" sx={{ mt: 2 }}>
          <CommonNumberInput
            placeholder="Offer Price"
            name="offerPrice"
            fullWidth
            value={form.offerPrice}
            onChange={handleInputChange}
          />
        </CommonFormControl>

        <CommonFormControl
          label="Street Address:"
          sx={{ mt: 2 }}
          error={
            showErrors && !form.address.street ? 'This field is required!' : ''
          }
        >
          <TextField
            placeholder="Street Address"
            name="address.street"
            fullWidth
            value={form.address.street}
            onChange={handleInputChange}
          />
        </CommonFormControl>

        <CommonFormControl
          label="City:"
          sx={{ mt: 2 }}
          error={
            showErrors && !form.address.city ? 'This field is required!' : ''
          }
        >
          <TextField
            placeholder="City"
            name="address.city"
            fullWidth
            value={form.address.city}
            onChange={handleInputChange}
          />
        </CommonFormControl>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6}>
            <CommonFormControl
              label="State:"
              sx={{ mt: 2 }}
              error={
                showErrors && !form.address.state
                  ? 'This field is required!'
                  : ''
              }
            >
              <Select
                name="address.state"
                value={form.address.state}
                onChange={handleSelectChange}
                fullWidth
                displayEmpty
              >
                <MenuItem value="">Select State</MenuItem>
                <MenuItem value="AL">Alabama</MenuItem>
                <MenuItem value="AK">Alaska</MenuItem>
                <MenuItem value="AZ">Arizona</MenuItem>
                <MenuItem value="AR">Arkansas</MenuItem>
                <MenuItem value="CA">California</MenuItem>
                <MenuItem value="CO">Colorado</MenuItem>
                <MenuItem value="CT">Connecticut</MenuItem>
                <MenuItem value="DE">Delaware</MenuItem>
                <MenuItem value="FL">Florida</MenuItem>
                <MenuItem value="GA">Georgia</MenuItem>
                <MenuItem value="HI">Hawaii</MenuItem>
                <MenuItem value="ID">Idaho</MenuItem>
                <MenuItem value="IL">Illinois</MenuItem>
                <MenuItem value="IN">Indiana</MenuItem>
                <MenuItem value="IA">Iowa</MenuItem>
                <MenuItem value="KS">Kansas</MenuItem>
                <MenuItem value="KY">Kentucky</MenuItem>
                <MenuItem value="LA">Louisiana</MenuItem>
                <MenuItem value="ME">Maine</MenuItem>
                <MenuItem value="MD">Maryland</MenuItem>
                <MenuItem value="MA">Massachusetts</MenuItem>
                <MenuItem value="MI">Michigan</MenuItem>
                <MenuItem value="MN">Minnesota</MenuItem>
                <MenuItem value="MS">Mississippi</MenuItem>
                <MenuItem value="MO">Missouri</MenuItem>
                <MenuItem value="MT">Montana</MenuItem>
                <MenuItem value="NE">Nebraska</MenuItem>
                <MenuItem value="NV">Nevada</MenuItem>
                <MenuItem value="NH">New Hampshire</MenuItem>
                <MenuItem value="NJ">New Jersey</MenuItem>
                <MenuItem value="NM">New Mexico</MenuItem>
                <MenuItem value="NY">New York</MenuItem>
                <MenuItem value="NC">North Carolina</MenuItem>
                <MenuItem value="ND">North Dakota</MenuItem>
                <MenuItem value="OH">Ohio</MenuItem>
                <MenuItem value="OK">Oklahoma</MenuItem>
                <MenuItem value="OR">Oregon</MenuItem>
                <MenuItem value="PA">Pennsylvania</MenuItem>
                <MenuItem value="RI">Rhode Island</MenuItem>
                <MenuItem value="SC">South Carolina</MenuItem>
                <MenuItem value="SD">South Dakota</MenuItem>
                <MenuItem value="TN">Tennessee</MenuItem>
                <MenuItem value="TX">Texas</MenuItem>
                <MenuItem value="UT">Utah</MenuItem>
                <MenuItem value="VT">Vermont</MenuItem>
                <MenuItem value="VA">Virginia</MenuItem>
                <MenuItem value="WA">Washington</MenuItem>
                <MenuItem value="WV">West Virginia</MenuItem>
                <MenuItem value="WI">Wisconsin</MenuItem>
                <MenuItem value="WY">Wyoming</MenuItem>
                <MenuItem value="DC">District of Columbia</MenuItem>
              </Select>
            </CommonFormControl>
          </Grid>
          <Grid item xs={6}>
            <CommonFormControl
              label="ZIP Code:"
              sx={{ mt: 2 }}
              error={
                showErrors && !form.address.zip ? 'This field is required!' : ''
              }
            >
              <TextField
                placeholder="ZIP Code"
                name="address.zip"
                fullWidth
                value={form.address.zip}
                onChange={handleInputChange}
                inputProps={{ maxLength: 5 }}
              />
            </CommonFormControl>
          </Grid>
        </Grid>

        <CommonFormControl label="Description:" sx={{ mt: 2 }}>
          <TextField
            placeholder="Description (optional)"
            name="description"
            fullWidth
            multiline
            rows={4}
            value={form.description}
            onChange={handleInputChange}
          />
        </CommonFormControl>
      </Box>
    </CommonDialog>
  );
};
